/* @flow */


import { apiName, apiVersion, getRandom, getTopLevelDomain } from '../utils';
import bidbase from './bidbase';
import { isFetchSupported, fetchJSON } from '../utils/fetch';
import adconfig from '../adconfig';
import { logError } from '../utils/logger';
import { extend, isArray, isUndefined, param } from '../utils/coreutils';
import { allSettledPromise } from '../utils/promiseutils';
import { isJSONSupported, isSecure, getLocationHref } from '../utils/browserutil';
import { getRTBUserEids } from '../idproviders/idputils';

/**
 * @module bidders/rubicon
 * @requires module:logger
 * @requires module:promiseutils
 */
/**
 * @global
 * @description extends {@link hbConfig}
 * @property {string} [qpKey=rub] //TODO - Do we need qp key?
 * @property {string} id ID
 */
/**
 * @description Implements functionality for rubicon header bidding
 * @exports rubiconbid
 * @augments _bidbase
 * @requires module:utils
 * @requires module:fetch
 * @requires module:adconfig
 * @requires module:coreutils
 */

/**
 * @global
 * @typedef hbPosData
 * @property {string} info Info to log
 * @property {object} _QP - bid value(cpm) are populated here
 * @property {object} _CV - partner specific pos id
 */

declare type rubSiteDetails = {
  account_id: string,
  site_id: string,
  p_screen_res: string,
  tk_flint: string,
  rp_secure: 0 | 1,
  rp_floor: 0.01,
  rf: string
};

declare type rubPosResponse = {
  status: 'no-ads' | 'ok',
  reason: string,
  error_code: number,
  script: string,
  cpm: string,
  impression_id: string
};
declare type rubResponse = { ads: Array<rubPosResponse> };

class _rubicon extends bidbase {
  _config: rubiconConfig;

  _DefConfig: rubiconConfig;

  constructor(name: string = 'rubicon') {
    super(name);
    this._DefConfig = extend(true, {}, this._DefConfig, {
      accountID: '20470',
      siteID: '249838',
      bidUrl: 'https://fastlane.rubiconproject.com/a/api/fastlane.json',
      qpKey: 'rub',
      batchCnt: 10
    });
    this._isSupported = isJSONSupported() && isFetchSupported();
    this._config = extend(true, {}, this._DefConfig);
    this._mandatoryConfigs = ['bidUrl', 'accountID', 'siteID'];
  }

  _getCreative(script: string = '', impId: string = ''): string {
    return `<div data-rp-impression-id='${impId}'>
    <script type='text/javascript'>${script}</script>
    </div>`;
  }

  _processBidResponse(
    posList: Array<string>,
    response: rubResponse,
    ret: hbPartnerData
  ): hbPartnerData {
    const { ads } = response;
    if (!isArray(ads) || !ads.length) {
      return ret;
    }
    const errInfo: Array<string> = [];
    posList.forEach((pos, index) => {
      const ad = ads[index] || {};
      if (ad.status === 'no-ads') {
        // nothing to do here
        return;
      }

      if (ad.status === 'ok') {
        if (ad.script && ad.impression_id && ad.cpm) {
          const adm = this._getCreative(ad.script, ad.impression_id);
          this._handleBidPosData(ret, pos, ad.cpm, ad.impression_id, adm);
        }
      } else {
        errInfo.push(
          `${pos}:${ad.status || ''}-${ad.error_code || -1}-${ad.reason || ''}`
        );
      }
    });
    if (errInfo.length) {
      // log the error now
      logError(`${this._name} bidding errors: ${errInfo.join(',')}`);
    }
    return ret;
  }

  _partitionArray(array: Array<string>, size: number): Array<Array<string>> {
    const posList = [...array];
    const partitions = [];
    // eslint-disable-next-line no-restricted-syntax
    while (posList.length) {
      partitions.push(posList.splice(0, size));
    }
    return partitions;
  }

  /* Get IDP info (User ID provider values) for bid request */
  _getIDPInfo(): anyObject {
    const { user: { eids = [] } = {} } = getRTBUserEids();

    const UID_MAP = {
      'liveramp.com': 'x_liverampidl',
      'adserver.org': ['eid_adserver.org', 'tpid_tdid']
    };
    // send ppuid
    const domain = getTopLevelDomain();
    // get supported userID value and append only the valid one
    return Object.fromEntries<string, string | Array<string>>(
      eids.reduce((acc, { source, uids: [{ id }] }) => {
        const mappedValue = UID_MAP[source];
        // if mapped value doesn't exist...
        if (source === domain) {
          acc.push(['ppuid', id]);
        } else if (isUndefined(mappedValue)) {
          acc.push([`eid_${source}`, id]);
        } else if (isArray(mappedValue)) {
          // $FlowFixMe[infer-error]
          acc.push(...mappedValue.map(v => [v, id]));
        } else {
          acc.push([mappedValue, id]);
        }
        return acc;
      }, [])
    );
  }

  _getSiteDetails(): rubSiteDetails {
    const siteInfo = {
      account_id: this._config.accountID,
      site_id: this._config.siteID,
      p_screen_res: `${window.screen.width}x${window.screen.height}`,
      tk_flint: `${apiName()}-${apiVersion()}`,
      rp_secure: isSecure() ? 1 : 0,
      rp_floor: 0.01,
      rf: getLocationHref()
    };
    return siteInfo;
  }

  _getSizeCodes(pos: string): Array<string> {
    const sizeMap = {
      '468x60': 1,
      '728x90': 2,
      '120x90': 5,
      '120x600': 8,
      '160x600': 9,
      '300x600': 10,
      '200x200': 13,
      '250x250': 14,
      '300x250': 15,
      '336x280': 16,
      '240x400': 17,
      '300x100': 19,
      '980x120': 31,
      '250x360': 32,
      '180x500': 33,
      '980x150': 35,
      '468x400': 37,
      '930x180': 38,
      '750x100': 39,
      '750x200': 40,
      '750x300': 41,
      '320x50': 43,
      '300x50': 44,
      '300x300': 48,
      '1024x768': 53,
      '300x1050': 54,
      '970x90': 55,
      '970x250': 57,
      '1000x90': 58,
      '320x80': 59,
      '320x150': 60,
      '1000x1000': 61,
      '580x500': 64,
      '640x480': 65,
      '930x600': 66,
      '320x480': 67,
      '1800x1000': 68,
      '320x320': 72,
      '320x160': 73,
      '980x240': 78,
      '980x300': 79,
      '980x400': 80,
      '480x300': 83,
      '970x310': 94,
      '970x210': 96,
      '480x320': 101,
      '768x1024': 102,
      '480x280': 103,
      '250x800': 105,
      '320x240': 108,
      '1000x300': 113,
      '320x100': 117,
      '800x250': 125,
      '200x600': 126,
      '980x600': 144,
      '320x250': 159,
      '250x600': 179,
      '600x300': 195,
      '640x360': 198,
      '640x200': 199,
      '1030x590': 213,
      '980x360': 214,
      '320x180': 229,
      '580x400': 232,
      '400x600': 257,
      '1920x1080': 265
    };
    return this._config.posTokenMap[pos].sizes.map(([w, h]) =>
      String(sizeMap[`${w}x${h}`] || 0)
    );
  }

  _getPositionsData(posList: Array<string>): Array<rubPosData> {
    const defaultPosData: rubPosData = this._getDefaultPosDetails();
    return posList.map(pos => {
      const [primarySize, ...altSizes] = this._getSizeCodes(pos);
      const gpid = adconfig.getGPID(pos);
      return extend(true, {}, defaultPosData, {
        zone_id: this._config.posTokenMap[pos].zone_id,
        size_id: primarySize,
        alt_size_ids: altSizes.join(','),
        p_pos: adconfig.isAboveFold(pos) ? 'atf' : 'btf',
        // eslint-disable-next-line camelcase
        p_gpid: gpid,
        'tg_i.dfp_ad_unit_code': gpid,
        'x_source.tid': getRandom()
      });
    });
  }

  _getDefaultPosDetails(): rubPosData {
    return {
      zone_id: '',
      size_id: '',
      alt_size_ids: '',
      p_pos: 'unknown',
      // eslint-disable-next-line camelcase
      p_gpid: '',
      'tg_i.dfp_ad_unit_code': '',
      'x_source.tid': ''
    };
  }

  _combinePositionData(posData: Array<rubPosData>): anyObject {
    const defPosObj = this._getDefaultPosDetails();

    // Combine details from all positions into a single object

    const combinedData = Object.keys(defPosObj).reduce((acc, k) => {
      acc[k] = posData.map(v => v[k]);
      return acc;
    }, {});

    // Filter keys with atleast one non-empty array contents
    const trimmedData = Object.keys(combinedData)
      .filter(v => combinedData[v].some(t => t.length > 0))

      .reduce((acc, v) => {
        acc[v] = combinedData[v].join(';');
        return acc;
      }, {});
    return trimmedData;
  }

  /**
   * get bids for given positions batch wise
   *
   * @param {Array<string>} posList - HB position list
   * @param {object.<string, hbPosData>} ret - Return data
   * @returns {Array<Promise<*>>} - Array of promises, one for each batch
   * @access private
   */
  _getBid(
    posList: Array<string>,
    ret: hbPartnerData
  ): Array<Promise<hbPartnerData>> {
    const siteData = this._getSiteDetails();
    const opt = {
      data: {},
      timeout: this._config.timeout,
      logPrefix: ''
    };
    const req = {
      method: 'GET',
      credentials: 'include'
    };
    // Send requests batch wise
    return this._partitionArray(posList, this._config.batchCnt).map(
      posGroup => {
        const positionsData: Array<rubPosData> =
          this._getPositionsData(posGroup);
        const combinedPosData = this._combinePositionData(positionsData);
        opt.data = extend(
          true,
          {},
          siteData,
          combinedPosData,
          {
            rand: getRandom(),
            slots: posGroup.length
          },
          this._getPrivacyString(),
          this._getIDPInfo() // Get Available IDP (from User ID modules) information
        );
        opt.logPrefix = `${this.getName()} - ${posGroup.join(',')}`;
        return fetchJSON(this._config.bidUrl, opt, req).then(response =>
          this._processBidResponse(posGroup, response, ret)
        );
      }
    );
  }

  /**
   * This method will be called for bidders witout overriden userSync method
   *
   * @external syncObj
   * @returns {Array<syncObj>} - Array of sync objects
   * @access private
   */
  _getUserSync(): Array<syncObj> {
    const ret = [];
    // make sure that syncUrl is not empty
    if (this._config.syncUrl.length) {
      ret.push({
        name: this.getName(),
        type: 'iframe',
        url: `${this._config.syncUrl}?${param(this._getPrivacyString())}`
      });
    }
    return ret;
  }

  /**
   * get bids for given positions
   *
   * @param {Array<string>} posList - HB position list
   * @param {object.<string, hbPosData>} ret - Return data
   * @returns {Promise<object.<string, hbPosData>>} - Promise
   * @access private
   */
  _getAds(posList: Array<string>, ret: hbPartnerData): Promise<hbPartnerData> {
    this._bidResponses = {};

    // Create promise array with all bid requests
    const pArray = this._getBid(posList, ret);
    return allSettledPromise(pArray).then(
      arr => {
        const fValues = arr
          .filter(v => v.state !== 'fulfilled')
          .map(v => v.reason);
        if (fValues.length) {
          // Log all failed promise errors
          logError(`${this._name} bidding errors: ${fValues.join(',')}`);
        }
        return ret;
      },
      error => {
        logError(`${this._name} bidding failed: ${error || ''}`);
        return ret;
      }
    );
  }
}

// add bidder to bidmanager
const rubicon: _rubicon = new _rubicon();
export default rubicon;
